import { createApp } from 'vue'
import App from './App.vue'
import { createI18n } from 'vue-i18n'
import { createHead } from '@vueuse/head'
import axios from 'axios'
import KinescopePlayer from '@kinescope/vue-kinescope-player'
// Router & Store
import router from './router.js'
import store from './store.js'
// Languages
import en from './assets/languages/en.json'
import ru from './assets/languages/ru.json'

// Configure axios
axios.defaults.baseURL = import.meta.env.VITE_API_URL || 'https://www.sotamedia.pro/api/'

// Add request interceptor for debugging
axios.interceptors.request.use(
    config => {
        console.log('Making request to:', config.url);
        return config;
    },
    error => {
        console.error('Request error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor for debugging
axios.interceptors.response.use(
    response => {
        console.log('Response received:', response.status, response.config.url);
        return response;
    },
    error => {
        console.error('Response error:', error.response?.status, error.config?.url, error.message);
        if (error.code === 'ERR_NETWORK') {
            console.error('Network error - possible CORS issue or API unavailable');
        }
        return Promise.reject(error);
    }
);

// Create event bus
import { createEventBus } from './utils/eventBus.js'
const bus = createEventBus()

// Язык определяется исключительно по URL:
// /en/... → английский
// /... (без /en) → русский (по умолчанию)
// Начальный locale установлен на русский, navigation guard обновит его при необходимости
const i18n = createI18n({
    legacy: false,
    locale: 'ru-RU',
    messages: {
        'en-US': en['messages'],
        'ru-RU': ru['messages']
    },
    datetimeFormats: {
        'en-US': en['dateTimeFormats'],
        'ru-RU': ru['dateTimeFormats']
    }
})

// Navigation guard для определения языка из URL
router.beforeEach((to, from, next) => {
    const lang = to.params.lang
    
    // Если это корневой путь без языка
    if (to.path === '/' && !lang) {
        const savedLang = localStorage.getItem('preferredLanguage')
        
        if (!savedLang) {
            // Определяем язык по локализации браузера
            const browserLang = navigator.language || (navigator.languages && navigator.languages[0]) || 'ru'
            const detectedLang = browserLang.toLowerCase().startsWith('en') ? 'en' : 'ru'
            localStorage.setItem('preferredLanguage', detectedLang)
            
            if (detectedLang === 'en') {
                return next('/en')
            }
        } else if (savedLang === 'en') {
            return next('/en')
        }
    }
    
    // Существующая логика определения языка из URL
    if (lang === 'en') {
        i18n.global.locale.value = 'en-US'
        document.documentElement.lang = 'en'
    } else {
        i18n.global.locale.value = 'ru-RU'
        document.documentElement.lang = 'ru'
    }
    next()
})

const app = createApp(App)
const head = createHead()

app.use(KinescopePlayer)

// Global properties
app.config.globalProperties.$http = axios
app.config.globalProperties.$bus = bus

// Mixin for language field checking, image URL handling, and localized URLs
app.mixin({
    computed: {
        isEnglish() {
            return this.$route?.params?.lang === 'en'
        }
    },
    methods: {
        checkLangField(field) {
            return (!!field && !!field[this.$i18n.locale])
        },
        getFullImageUrl(imagePath) {
            // Если путь уже полный (содержит http/https), возвращаем как есть
            if (imagePath && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) {
                return imagePath;
            }
            // Если путь относительный, добавляем базовый URL
            if (imagePath && imagePath.startsWith('/')) {
                return this.$store.state.url + imagePath;
            }
            // Если путь без слеша, добавляем базовый URL и слеш
            if (imagePath && !imagePath.startsWith('/')) {
                return this.$store.state.url + '/' + imagePath;
            }
            return imagePath;
        },
        getLocalizedUrl(url) {
            // Добавляет /en префикс к URL если текущий язык английский
            if (this.isEnglish && url && !url.startsWith('/en') && !url.startsWith('http')) {
                return '/en' + (url === '/' ? '' : url)
            }
            return url
        }
    }
})

app.use(router)
app.use(store)
app.use(i18n)
app.use(head)

router.isReady().then(() => app.mount('#app'))

export { i18n }
export default bus
